import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import type {
  InstallTemplateResponse,
  ListTemplatesResponse,
  TemplateSummary,
} from '@shared/types/templates';

const isListTemplatesResponse = (value: unknown): value is ListTemplatesResponse => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const record = value as { templates?: unknown };
  return Array.isArray(record.templates);
};

export const TemplatesPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isOnboarding = searchParams.get('onboarding') === '1';
  const focusedTemplateKey = searchParams.get('template');
  const source = searchParams.get('source');
  const [templates, setTemplates] = useState<TemplateSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [installingKey, setInstallingKey] = useState<string | null>(null);

  const orderedTemplates = useMemo(() => {
    if (!focusedTemplateKey) return templates;

    return [...templates].sort((left, right) => {
      if (left.key === focusedTemplateKey) return -1;
      if (right.key === focusedTemplateKey) return 1;
      return 0;
    });
  }, [focusedTemplateKey, templates]);

  const loadTemplates = async () => {
    setLoading(true);
    setError(null);

    const { data, error: invokeError } = await supabase.functions.invoke('list-templates', {
      body: {},
    });

    if (invokeError) {
      setError(invokeError.message);
      setLoading(false);
      return;
    }

    const payload = data as unknown;
    if (!isListTemplatesResponse(payload)) {
      const errorPayload = payload as { error?: string } | null;
      setError(errorPayload?.error ?? 'Réponse invalide du serveur');
      setLoading(false);
      return;
    }

    setTemplates(payload.templates ?? []);
    setLoading(false);
  };

  useEffect(() => {
    void loadTemplates();
  }, []);

  const handleInstall = async (templateKey: string) => {
    setInstallingKey(templateKey);
    setError(null);

    try {
      const { data, error: invokeError } = await supabase.functions.invoke('install-template', {
        body: { templateKey },
      });

      if (invokeError) {
        setError(invokeError.message);
        return;
      }

      const payload = data as InstallTemplateResponse | { error?: string } | null;
      if (!payload || 'error' in payload || !('flow_id' in payload)) {
        setError((payload as { error?: string } | null)?.error ?? 'Réponse invalide du serveur');
        return;
      }

      await loadTemplates();
      navigate(payload.redirect || '/');
    } finally {
      setInstallingKey(null);
    }
  };

  return (
    <div className="mx-auto max-w-6xl p-8">
      <div className="mb-8">
        <h1 className="font-display text-2xl font-bold text-gray-900">Templates</h1>
        <p className="mt-1 text-sm text-gray-500">
          Activez des flows prêtes à l’emploi puis adaptez-les à votre équipe.
        </p>
      </div>

      {isOnboarding && (
        <Card className="mb-4 border-domino-cardBorder bg-domino-lilac">
          <p className="text-sm text-domino-aubergine">
            Commencez ici: installez un template pour peupler automatiquement votre dashboard.
          </p>
        </Card>
      )}

      {source === 'dashboard_chat' && focusedTemplateKey && (
        <Card className="mb-4 border-domino-cardBorder bg-domino-lilac">
          <p className="text-sm text-domino-aubergine">
            Le chat ne construit ni n&apos;installe cette flow inline. Il t&apos;oriente vers un template structure que tu peux ouvrir et installer explicitement.
          </p>
        </Card>
      )}

      {error && (
        <Card className="mb-4 border-red-200 bg-red-50">
          <p className="text-sm text-red-700">{error}</p>
        </Card>
      )}

      {loading ? (
        <div className="flex h-40 items-center justify-center">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-gray-900" />
        </div>
      ) : templates.length === 0 ? (
        <Card>
          <p className="text-sm text-gray-500">Aucun template disponible pour le moment.</p>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {orderedTemplates.map((template) => {
            const hasMissingIntegration = template.integration_status.some((status) => !status.connected);
            const isFocused = template.key === focusedTemplateKey;
            return (
              <Card
                key={template.key}
                className={isFocused
                  ? 'flex h-full flex-col border-domino-prune ring-1 ring-domino-lilac'
                  : 'flex h-full flex-col'}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">{template.title}</h2>
                    <p className="mt-1 text-sm text-gray-600">{template.description}</p>
                  </div>
                  <Badge variant={template.installed ? 'success' : 'outline'}>
                    {template.installed ? 'Installé' : 'Non installé'}
                  </Badge>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  {template.tags.map((tag) => (
                    <Badge key={tag} variant="outline">{tag}</Badge>
                  ))}
                  <Badge variant="secondary">{template.view_type}</Badge>
                  {isFocused && (
                    <Badge variant="secondary" className="bg-domino-lilac text-domino-aubergine">
                      Recommande depuis le chat
                    </Badge>
                  )}
                </div>

                <div className="mt-4 space-y-1 text-xs text-gray-500">
                  {template.required_integrations.length === 0 ? (
                    <p>Aucune intégration requise.</p>
                  ) : (
                    template.integration_status.map((integration) => (
                      <p key={`${template.key}-${integration.provider}`}>
                        {integration.connected ? 'Connecté' : 'À connecter'}: {integration.provider}
                      </p>
                    ))
                  )}
                </div>

                <div className="mt-5 flex flex-wrap gap-2">
                  {!template.installed ? (
                    <Button
                      onClick={() => handleInstall(template.key)}
                      loading={installingKey === template.key}
                    >
                      Installer
                    </Button>
                  ) : (
                    <>
                      {template.installed_flow_id && (
                        <Button
                          onClick={() => navigate(`/tools/${encodeURIComponent(template.installed_flow_id ?? '')}`)}
                        >
                          Ouvrir la flow
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        onClick={() => handleInstall(template.key)}
                        loading={installingKey === template.key}
                      >
                        Réinstaller le widget
                      </Button>
                    </>
                  )}
                </div>

                {hasMissingIntegration && (
                  <p className="mt-4 text-xs text-amber-700">
                    Certaines intégrations requises ne sont pas connectées.
                  </p>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
